import React from 'react';
import { Folder, AudioFile } from '../../types';
import {
  FolderIcon,
  MusicNoteIcon,
  EditIcon,
  DeleteIcon,
  ChevronRightIcon,
} from '../../icons';
import FileUploadArea from './FileUploadArea';

interface FolderTreeProps {
  folders: Folder[];
  files: AudioFile[];
  currentFolderId?: string | null;
  expandedFolders: Set<string>;
  draggedFile: AudioFile | null;
  dragOverFolder: string | null;
  searchQuery: string;
  acceptedFormats: string[];
  isUploading: boolean;
  selectedFolder: any;
  selectedAudioFile: AudioFile | null;

  // Helper functions
  getRootFiles: () => AudioFile[];
  getFolderFiles: (folderId: string) => AudioFile[];
  shouldShowFolderFiles: (folderId: string) => boolean;
  allRelevantFolders: Folder[];
  filteredFiles: AudioFile[];

  // Event handlers
  onFolderSelect: (folderId: string | null) => void;
  toggleFolder: (folderId: string) => void;
  expandOnlyFolder: (folderId: string) => void;
  onFilesUpload: (files: FileList) => void;
  onFileUploaded?: (file: AudioFile) => void;
  onUploadError?: (error: string) => void;
  onFolderDragOver: (e: React.DragEvent, folderId: string | null) => void;
  onFolderDragLeave: (e: React.DragEvent) => void;
  onFolderDrop: (e: React.DragEvent, folderId: string | null) => void;
  onFileDragStart: (file: AudioFile) => void;
  onFileDragEnd: () => void;
  onFileSelect: (file: AudioFile) => void;
  onEditFolder: (folder: Folder) => void;
  onDeleteFolder: (folder: Folder) => void;
  onEditFile: (file: AudioFile, e: React.MouseEvent) => void;
  onDeleteFile: (file: AudioFile) => void;
}

const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  files,
  currentFolderId,
  expandedFolders,
  draggedFile,
  dragOverFolder,
  searchQuery,
  acceptedFormats,
  isUploading,
  selectedFolder,
  selectedAudioFile,
  getRootFiles,
  getFolderFiles,
  shouldShowFolderFiles,
  allRelevantFolders,
  filteredFiles,
  onFolderSelect,
  toggleFolder,
  expandOnlyFolder,
  onFilesUpload,
  onFileUploaded,
  onUploadError,
  onFolderDragOver,
  onFolderDragLeave,
  onFolderDrop,
  onFileDragStart,
  onFileDragEnd,
  onFileSelect,
  onEditFolder,
  onDeleteFolder,
  onEditFile,
  onDeleteFile,
}) => {
  const renderFile = (file: AudioFile, level: number = 0) => (
    <div
      key={file.id}
      className={`group transition-all ${
        draggedFile?.id === file.id ? 'opacity-50' : ''
      }`}
      style={{ paddingLeft: `${(level + 1) * 20 + 8}px` }}
    >
      <div
        draggable
        onDragStart={() => onFileDragStart(file)}
        onDragEnd={onFileDragEnd}
        onClick={() => onFileSelect(file)}
        className={`flex items-center justify-between p-1 rounded-md cursor-pointer transition-all ${
          selectedAudioFile?.id === file.id
            ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
            : 'hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <MusicNoteIcon size={16} className="text-gray-400" />
          <div
            className={`w-2 h-2 rounded-full ${
              file.status === 'ready'
                ? 'bg-green-500'
                : file.status === 'processing'
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
            }`}
          />
          <div className="flex-1 min-w-0">
            <span className="text-sm text-gray-900 dark:text-gray-100 truncate block">
              {file.name}
            </span>
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-2">
              <span>{file.fileSizeDisplay}</span>
              {file.durationDisplay && <span>• {file.durationDisplay}</span>}
              <span>• {file.contentType?.split('/')[1]?.toUpperCase()}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100">
            <button
              onClick={(e) => onEditFile(file, e)}
              className="p-1 text-gray-400 hover:text-blue-600"
              title="Edit name"
            >
              <EditIcon size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteFile(file);
              }}
              className="p-1 text-gray-400 hover:text-red-600"
              title="Delete file"
            >
              <DeleteIcon size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-1" data-tutorial="file-tree">
      {/* Root folder */}
      <div>
        <div
          className={`group flex items-center justify-between p-1 rounded-md cursor-pointer transition-colors ${
            selectedFolder?.id === 'common'
              ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
              : 'hover:bg-gray-50 dark:hover:bg-gray-800'
          } ${
            dragOverFolder === null &&
            draggedFile &&
            draggedFile.folderId !== null
              ? 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-900/20'
              : ''
          }`}
          onClick={() => {
            onFolderSelect(null);
            expandOnlyFolder('common');
          }}
          onDragOver={(e) => onFolderDragOver(e, null)}
          onDragLeave={onFolderDragLeave}
          onDrop={(e) => onFolderDrop(e, null)}
        >
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder('common');
              }}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            >
              <ChevronRightIcon
                size={16}
                className={`text-gray-500 transition-transform ${
                  expandedFolders.has('common') ? 'rotate-90' : ''
                }`}
              />
            </button>
            <FolderIcon size={16} className="text-amber-600" />
            <span className="text-sm text-gray-900 dark:text-gray-100 font-medium">
              Common
            </span>
          </div>
          <span className="text-xs text-gray-500">{getRootFiles().length}</span>
        </div>

        {/* Root files */}
        {(expandedFolders.has('common') ||
          (searchQuery.trim() && getRootFiles().length > 0)) && (
          <>
            {getRootFiles().map((file) => renderFile(file, 0))}
            {/* File Upload Area inside Common folder */}
            {expandedFolders.has('common') && (
              <div className="ml-6 mt-2">
                <FileUploadArea
                  onFilesSelected={onFilesUpload}
                  onFileUploaded={onFileUploaded}
                  onUploadError={onUploadError}
                  currentFolderId={currentFolderId}
                  isUploading={isUploading}
                  acceptedFormats={acceptedFormats}
                  hasFiles={files.length > 0}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Regular folders */}
      {allRelevantFolders.map((folder) => (
        <div key={folder.id}>
          <div
            className={`group flex items-center justify-between p-1 rounded-md cursor-pointer transition-colors ${
              selectedFolder?.id === folder.id
                ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
                : 'hover:bg-gray-50 dark:hover:bg-gray-800'
            } ${
              dragOverFolder === folder.id &&
              draggedFile &&
              draggedFile.folderId !== folder.id
                ? 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-900/20'
                : ''
            }`}
            onClick={() => {
              onFolderSelect(folder.id);
              expandOnlyFolder(folder.id);
            }}
            onDragOver={(e) => onFolderDragOver(e, folder.id)}
            onDragLeave={onFolderDragLeave}
            onDrop={(e) => onFolderDrop(e, folder.id)}
          >
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolder(folder.id);
                }}
                className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              >
                <ChevronRightIcon
                  size={16}
                  className={`text-gray-500 transition-transform ${
                    expandedFolders.has(folder.id) ? 'rotate-90' : ''
                  }`}
                />
              </button>
              <FolderIcon size={16} className="text-blue-600" />
              <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                {folder.name}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditFolder(folder);
                  }}
                  className="p-1 text-gray-400 hover:text-blue-600"
                  title="Edit folder"
                >
                  <EditIcon size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteFolder(folder);
                  }}
                  className="p-1 text-gray-400 hover:text-red-600"
                  title="Delete folder"
                >
                  <DeleteIcon size={16} />
                </button>
              </div>
              <span className="text-xs text-gray-500">
                {getFolderFiles(folder.id).length}
              </span>
            </div>
          </div>

          {/* Folder files */}
          {shouldShowFolderFiles(folder.id) && (
            <>
              {getFolderFiles(folder.id).map((file) => renderFile(file, 0))}
              {/* File Upload Area inside expanded folder */}
              {expandedFolders.has(folder.id) && (
                <div className="ml-6 mt-2">
                  <FileUploadArea
                    onFilesSelected={onFilesUpload}
                    onFileUploaded={onFileUploaded}
                    onUploadError={onUploadError}
                    currentFolderId={currentFolderId}
                    isUploading={isUploading}
                    acceptedFormats={acceptedFormats}
                    hasFiles={files.length > 0}
                  />
                </div>
              )}
            </>
          )}
        </div>
      ))}

      {/* Empty State */}
      {searchQuery.trim() &&
        allRelevantFolders.length === 0 &&
        filteredFiles.length === 0 && (
          <div className="py-8 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No files or folders found.
            </p>
          </div>
        )}
    </div>
  );
};

export default FolderTree;
